// Shared MapLibre image-registry helpers.
//
// Six icon modules under src/map/* (airportIcons, fixIcons, fuelIcons,
// navaidIcons, skyCoverIcons, windBarbs) used to ship their own
// byte-identical loadHtmlImage() and a near-identical ensureXxxIcons()
// loop. This module consolidates both.
//
// loadMapImage(url, w, h)
//   Cross-origin <img> loader returning a Promise<HTMLImageElement>.
//   Single helper used by every icon module.
//
// ensureSvgImages(map, options)
//   Renders a list of named SVG strings into MapLibre's image atlas
//   at the device pixel ratio. Idempotent (skips existing images).
//   Per-symbol size override supports modules whose icons aren't all
//   the same dimension (e.g. airportIcons mixes 32px heli/wxcam with
//   40px airport rings).
//
// All callers fire-and-forget; MapLibre re-renders the layer that
// references the image once it lands in the atlas, so caller loops
// are non-blocking.

import type { Map as MlMap } from 'maplibre-gl';

/**
 * Load a remote / data-URL into an HTMLImageElement at a chosen
 * pixel size. We force `crossOrigin = 'anonymous'` because the
 * resulting <img> ends up rasterized into a WebGL texture; the
 * canvas/texture pipeline is taint-poisoned by non-CORS images.
 */
export function loadMapImage(url: string, w: number, h: number): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image(w, h);
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(new Error(String(e)));
    img.crossOrigin = 'anonymous';
    img.src = url;
  });
}

/** Symbol descriptor consumed by ensureSvgImages. */
export interface SvgSymbol {
  /** MapLibre image id (e.g. 'fuel-100ll'). */
  id: string;
  /** Complete <svg> markup. The viewBox must be square / matched to size. */
  svg: string;
  /**
   * Optional per-symbol pixel size override. Falls back to
   * options.defaultSize. Use this when one module renders icons at
   * mixed sizes (airport icons: 40px airports, 32px heli/wxcam).
   */
  size?: number;
}

interface EnsureOpts {
  /**
   * Square pixel size used to render every symbol unless that
   * symbol carries its own `size` override.
   */
  defaultSize: number;
  /**
   * Tag used in console.warn output when a single symbol fails
   * to load. Helpful when triaging which icon module had the issue.
   * Example: '[airportIcons]'.
   */
  logTag: string;
  /**
   * Cap the rendered pixel ratio. We default to 2 because Retina
   * iPad already gets crisp icons there and 3x over-renders without
   * visual benefit (MapLibre's icon-size compensates).
   */
  pixelRatioCap?: number;
}

/**
 * Render each SvgSymbol into MapLibre's image atlas. Idempotent: if
 * `map.hasImage(id)` is already true the symbol is skipped (so the
 * caller can call this multiple times across map style changes
 * without worrying about double-registration).
 *
 * Called fire-and-forget; the returned Promise resolves once every
 * symbol has been registered (or has logged its failure).
 */
export async function ensureSvgImages(
  map: MlMap,
  symbols: ReadonlyArray<SvgSymbol>,
  opts: EnsureOpts,
): Promise<void> {
  const cap = Math.max(1, opts.pixelRatioCap ?? 2);
  const dpr = Math.min(cap, Math.max(1, Math.round(window.devicePixelRatio || 1)));

  await Promise.all(
    symbols.map(async (sym) => {
      if (map.hasImage(sym.id)) return;
      const baseSize = sym.size ?? opts.defaultSize;
      try {
        const url = 'data:image/svg+xml;utf8,' + encodeURIComponent(sym.svg);
        const img = await loadMapImage(url, baseSize * dpr, baseSize * dpr);
        if (!map.hasImage(sym.id)) {
          map.addImage(sym.id, img as unknown as ImageData, { pixelRatio: dpr });
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn(`${opts.logTag} failed to load`, sym.id, e);
      }
    }),
  );
}
